import { zodResolver } from "@hookform/resolvers/zod";
import useToast from "@repo/core/components/core/Toast";
import { IActionsheetRefProps } from "@repo/core/components/layout/Actionsheet/types";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import {
  IModalOption,
  IModalTemplateType
} from "@repo/core/components/layout/ModalTemplate/types";

import { useNavigation } from "@repo/core/hooks/useNavigation";
import { usePreProposalStore } from "../../store/preProposalStore";
import { apiInstance } from "@repo/core/services/instances/api";
import { wayupApiInstance } from "@repo/core/services/instances/wayup-api";
import { CommonResource } from "@repo/core/services/resources/common";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import { applyMaskToValue } from "@repo/core/utils/mask.utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InteractionManager } from "react-native";

import { useForm } from "react-hook-form";
import z from "zod";
import { Permission } from "@repo/core/constants/permissions";
import { useResendCodeConfig } from "@repo/core/hooks/useResendCodeConfig";
import { useUserPermissions } from "@repo/core/hooks/useUserPermissions";
import { useAuthStore } from "@repo/core/stores/auth";
import { useQuery } from "@tanstack/react-query";
import { useResolveRequiredDocumentsAfterSecurity } from "../../hooks/useResolveRequiredDocumentsAfterSecurity";
import { continueAfterValidatedProposalPhoneToken } from "../../services/proposalPhoneTokenStatus.service";
import { runSkipTokenVerification } from "./services/runSkipTokenVerification.service";

const formSchema = z.object({
  code: z
    .string({ required_error: "O código é obrigatório" })
    .length(6, "O código deve ter 6 dígitos")
});

export type FormDataType = z.infer<typeof formSchema>;

const commonResource = new CommonResource(apiInstance);
const requisitionsResource = new RequisitionsResource(wayupApiInstance);

export const ConfirmTokenController = () => {
  const actionSheetRef = useRef<IActionsheetRefProps>(null);
  const alertModalRef = useRef<IModalRefProps>(null);
  const { expiresInSeconds, fetchResendConfig } = useResendCodeConfig();
  const { hasPermission } = useUserPermissions();
  const { user: authUser } = useAuthStore();

  const [isResendingToken, setIsResendingToken] = useState(false);
  const [isSkippingToken, setIsSkippingToken] = useState(false);
  const [shouldEnableResend, setShouldEnableResend] = useState(false);
  const [otpInputKey, setOtpInputKey] = useState(0);
  const isSkippingTokenRef = useRef(false);

  const { back } = useNavigation();
  const proposalData = usePreProposalStore((state) => state.proposalData);
  const { success, error } = useToast();
  const { resolveRequiredDocumentsAfterSecurity } =
    useResolveRequiredDocumentsAfterSecurity();

  const cellPhone = useMemo(
    () =>
      applyMaskToValue(
        `${proposalData?.strEndDdd1?.toString()}${proposalData?.strEndFone1?.toString()}` ||
          "",
        "cellphone"
      ),
    [proposalData?.strEndDdd1, proposalData?.strEndFone1]
  );

  const handleCloseAlert = useCallback(() => {
    alertModalRef.current?.close();
  }, []);

  const handleOpenActionSheet = useCallback(() => {
    actionSheetRef.current?.open();
  }, []);

  const alertOptions: IModalOption[] = useMemo(
    () => [
      {
        label: "Tentar novamente",
        onPress: handleCloseAlert
      },
      {
        label: "Ajuda",
        onPress: handleOpenActionSheet,
        type: IModalTemplateType.outline,
        leftIcon: {
          name: "circle-question",
          variant: "solid",
          size: 18,
          color: "$textLight800"
        }
      }
    ],
    [handleCloseAlert, handleOpenActionSheet]
  );

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
    clearErrors
  } = useForm<FormDataType>({
    resolver: zodResolver(formSchema)
  });

  const hasStoredPermissionToDispenseToken = useMemo(
    () =>
      hasPermission([Permission.CRT_WEBAPI_PROPOSTA_DISPENSAR_TOKEN_ACEITE]),
    [hasPermission]
  );

  const { data: hasLegacyPermissionToDispenseToken = false } = useQuery({
    queryKey: [
      "hasPermissionToDispenseToken",
      authUser?.tokenData.strUsuario,
      authUser?.token
    ],
    queryFn: async () => {
      const { data } = await commonResource.direitoAcesso({
        strUser: authUser?.tokenData.strUsuario.trim() || "",
        strProcesso: `'${Permission.CRT_WEBAPI_PROPOSTA_DISPENSAR_TOKEN_ACEITE}'`
      });

      return data;
    },
    enabled:
      !hasStoredPermissionToDispenseToken && !!authUser?.tokenData.strUsuario,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const hasPermissionToDispenseToken =
    hasStoredPermissionToDispenseToken || hasLegacyPermissionToDispenseToken;

  const handleMoreInformation = useCallback(() => {
    actionSheetRef.current?.open();
  }, []);

  const handleResendCode = useCallback(async () => {
    try {
      reset({ code: "" });
      clearErrors("code");
      setOtpInputKey((prevKey) => prevKey + 1);
      setIsResendingToken(true);

      const { data: generateTokenResponse } =
        await commonResource.generateToken({
          strDDD: proposalData?.strEndDdd1?.toString() || "",
          strTelefone: proposalData?.strEndFone1?.toString() || "",
          strCliente: proposalData?.strCliente?.toString() || "",
          strNome: proposalData?.strNome?.toString() || "",
          strProduto: proposalData?.strProduto?.toString() || ""
        });

      if (generateTokenResponse.blnSms) {
        success("Token reenviado");
      } else {
        error("Erro ao reenviar o token, tente novamente.");
      }
    } catch (err) {
      console.error("Erro ao reenviar o token", err);
      error("Erro ao reenviar o token, tente novamente.");
    } finally {
      setIsResendingToken(false);
    }
  }, [proposalData, success, error, reset, clearErrors]);

  const handleSkipTokenVerification = useCallback(() => {
    return runSkipTokenVerification(
      isSkippingTokenRef,
      setIsSkippingToken,
      resolveRequiredDocumentsAfterSecurity
    );
  }, [resolveRequiredDocumentsAfterSecurity]);

  const submitToken = useCallback(
    async (data: FormDataType) => {
      try {
        const { data: validateTokenResponse } =
          await commonResource.validateToken({
            strToken: data.code,
            strCliente: proposalData?.strCliente?.toString() || "",
            strFormaEnvio: "S"
          });

        if (validateTokenResponse) {
          await continueAfterValidatedProposalPhoneToken(
            requisitionsResource,
            proposalData || {},
            resolveRequiredDocumentsAfterSecurity
          );
        } else {
          error(
            "Token inválido. Verifique o código informado ou solicite o reenvio após o término do prazo de validade."
          );
          setShouldEnableResend(true);
        }
      } catch (err) {
        console.error("Erro ao validar o token", err);
        error("Erro ao validar o token, por favor tente novamente.");
      }
    },
    [proposalData, resolveRequiredDocumentsAfterSecurity, error]
  );

  const handleSubmitForm = useMemo(
    () => handleSubmit(submitToken),
    [handleSubmit, submitToken]
  );

  useEffect(() => {
    if (expiresInSeconds > 0) return;

    const interactionTask = InteractionManager.runAfterInteractions(() => {
      void fetchResendConfig();
    });

    return () => {
      interactionTask.cancel();
    };
  }, [expiresInSeconds, fetchResendConfig]);

  return {
    control,
    cellPhone,
    handleResendCode,
    back,
    alertModalRef,
    actionSheetRef,
    handleMoreInformation,
    isResendingToken,
    alertOptions,
    handleSubmitForm,
    isSubmitting,
    hasPermissionToDispenseToken,
    handleSkipTokenVerification,
    isSkippingToken,
    shouldEnableResend,
    setShouldEnableResend,
    otpInputKey,
    expiresInSeconds
  };
};
